import React from 'react';
import styled from 'styled-components';
import { Box, Flex } from 'rebass';
import PropTypes from 'prop-types';

import { Account } from '@features/common';
import { SkeletonRenderer } from '@lib/skeleton-loader';
import { Trans } from '@lingui/macro';
import { Edit, Comment, AddUser } from '@oca/icons';
import { Typography, Button, AssigneeList } from '@oca/ui';

export const ViewHeader = ({
  isLoading,
  author,
  comments = [],
  members = [],
  onEdit,
  title,
  task,
}) => {
  const { description } = task || {
    description: '',
  };
  return (
    <HeaderContainer paddingTop={24} paddingBottom={2} flexDirection="column">
      <SkeletonRenderer
        loading={isLoading}
        render={() => (
          <Typography variant="h2" marginBottom={2}>
            {title}
          </Typography>
        )}
        width={200}
        height={20}
        boxProps={{ marginBottom: 2 }}
      />
      <Flex
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
        style={{ gap: 12 }}
      >
        <Flex alignItems="center" flexWrap="wrap" style={{ gap: 24 }}>
          <Flex
            alignItems="center"
            backgroundColor="secondary"
            px={3}
            py={1}
            style={{ gap: 8, borderRadius: 8 }}
          >
            <AddUser size={20} />
            <Typography color="textSecondary">Members:</Typography>
            <Box marginLeft={2}>
              <SkeletonRenderer
                loading={isLoading}
                render={() => <AssigneeList assignees={members} />}
                width={60}
                height={26}
                viewBox="0 0 60 26"
              >
                <circle cx="13" cy="13" r="13" />
                <circle cx="26" cy="13" r="13" />
                <circle cx="39" cy="13" r="13" />
              </SkeletonRenderer>
            </Box>
          </Flex>
          <Flex
            backgroundColor="secondary"
            px={3}
            py={2}
            style={{ gap: 8, borderRadius: 8 }}
            alignItems="center"
          >
            <Comment fontSize={20} />
            <Typography color="textSecondary">
              <Trans>Comments</Trans>
              :&nbsp;
              {comments && !isLoading ? comments.length : '-'}
            </Typography>
          </Flex>
        </Flex>
        <Account
          render={({ user }) => (
            <React.Fragment>
              {author && author.id === user.id && (
                <Button variant="secondary" onClick={onEdit}>
                  <Edit />
                </Button>
              )}
            </React.Fragment>
          )}
        />
      </Flex>
      <Box mt={10}>
        <SkeletonRenderer
          width={1}
          height={70}
          viewBox="0 0 290 70"
          loading={isLoading}
          render={() => (
            <Box style={{ minHeight: 30 }} marginTop={2}>
              <Typography color="textSecondary">
                {description || <Trans>No description</Trans>}
              </Typography>
            </Box>
          )}
        >
          <rect x="0" y="0" rx="3" ry="3" width="70" height="10" />
          <rect x="80" y="0" rx="3" ry="3" width="100" height="10" />
          <rect x="190" y="0" rx="3" ry="3" width="10" height="10" />
          <rect x="0" y="20" rx="3" ry="3" width="130" height="10" />
          <rect x="155" y="20" rx="3" ry="3" width="130" height="10" />
          <rect x="0" y="40" rx="3" ry="3" width="90" height="10" />
          <rect x="115" y="40" rx="3" ry="3" width="60" height="10" />
          <rect x="185" y="40" rx="3" ry="3" width="60" height="10" />
          <rect x="0" y="60" rx="3" ry="3" width="30" height="10" />
        </SkeletonRenderer>
      </Box>
    </HeaderContainer>
  );
};
ViewHeader.propTypes = {
  // eslint-disable-next-line react/require-default-props
  author: PropTypes.shape({
    id: PropTypes.number,
  }),
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    }),
  ),
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  members: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  ),
  task: PropTypes.shape({
    comments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ),
    description: PropTypes.node,
    id: PropTypes.number,
  }),
};
ViewHeader.defaultProps = {
  isLoading: false,
  comments: [],
  members: [],
  title: '',
  task: { attachments: [], comments: [], description: '' },
};

const HeaderContainer = styled(Flex)`
  position: relative;
`;
